import { attr, belongsTo } from "@ember-data/model";
import type { Dayjs, ManipulateType } from "dayjs";
import dayjs from "dayjs";
import {
  validateDate,
  validateInclusion,
  validateNumber,
  validatePresence
} from "ember-changeset-validations/validators";
import type { IntlService } from "ember-intl";
import type { default as JournalEventModel } from "parog-web/models/journal-event";
import { default as JournalEvent } from "parog-web/models/journal-event";
import type Patient from "parog-web/models/patient";
import tWithDefault from "parog-web/utils/t-with-default";
import type TreatmentHistoryChangedJournalEvent from "./treatment-history-changed-journal-event";

export interface BaseTaskPlanned {
  id: string;
  patient: Patient;
  occurredAt: Date;
  taskType: string;
  taskLabel: string;
  originTaskIdentifier: string;
  responsibleType: "patient" | "medic";
  responsibleId: number;
  remindResponsible: boolean;
  processingJournalEventType: string;
  originJournalEvent: JournalEventModel;
  isRecursiveTask?: true;
}

//---- Automatically generated, don't update this part ----

type ResponsibleTypeStringEnum = "patient";

/**
 * Represents a task that repeats over time
 */
export default class RecursiveTaskPlannedJournalEvent extends JournalEvent {
  static override type = "recursive-task-planned-journal-event";

  /**
   * The date where the recursivity stops
   */
  @attr("date") declare endDate: Date;

  /**
   * The number of time-frequency-unit
   */
  @attr("number") declare timeFrequencyValue: number;

  /**
   * The time unit used (days, months, weeks ...)
   */
  @attr("string") declare timeFrequencyUnit: string;

  /**
   * An identifier to be able to link the task to a JournalEvent field (mainly used by TreatmentHistoryJournalEvent)
   */
  @attr("string") declare originTaskIdentifier: string;

  /**
   * The type of the responsible. Could be null.
   */
  @attr("string") declare responsibleType: ResponsibleTypeStringEnum;

  /**
   * The id of the responsible, could be null
   */
  @attr("number") declare responsibleId: number;

  /**
   * An id describing the nature of the task.
   */
  @attr("string") declare taskType: string;

  /**
   * A boolean to remind the responsible to do the task
   */
  @attr("boolean", {
    allowNull: true
  })
  declare remindResponsible: boolean;

  /**
   * A JournalEvent type that will process the task automatically after a JE creation. Could be a CompoundSurvey's id if the taskType is "compound-survey".
   * This should be written in snake case.
   * Example: 'weight-measured-journal-event'
   */
  @attr("string") declare processingJournalEventType: string;

  /**
   * Represents the last time an occurrence has been processed
   */
  @attr("date") declare processedAt: Date;

  @belongsTo("treatment-history-changed-journal-event", {
    async: false,
    inverse: "recursiveTaskPlannedJournalEvents"
  })
  declare treatmentHistoryChangedJournalEvent: TreatmentHistoryChangedJournalEvent;

  static override validations(intl: IntlService) {
    return {
      occurredAt: [
        validatePresence({
          presence: true,
          description: tWithDefault(
            intl,
            "validations.recursive-task-planned-journal-event.occurred-at",
            "La date"
          )
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée avant 1900. Veuillez modifier la date",
          onOrAfter: new Date("01/01/1900")
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée plus de 50 ans en avance. Veuillez modifier la date",
          onOrBefore: dayjs().add(50, "years").toDate()
        })
      ],
      timeFrequencyValue: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.recursive-task-planned-journal-event.time-frequency-value"
        )
      }),
      responsibleId: validateNumber({
        allowBlank: true,
        integer: true,
        description: intl.t(
          "validations.recursive-task-planned-journal-event.responsible-id"
        )
      }),
      responsibleType: validateInclusion({
        list: ["patient"],
        allowBlank: true,
        description: intl.t(
          "validations.recursive-task-planned-journal-event.responsible-type"
        )
      })
    };
  }

  //---- End of generated code ----

  @belongsTo("patient", {
    async: false,
    inverse: "recursiveTaskPlannedJournalEvents"
  })
  declare patient: Patient;

  get lastOrNextUnprocessedTask(): BaseTaskPlanned | undefined {
    if (!this.processedAt) return this.createTaskAt(this.occurredAt);
    const date = this.nextDateFrom(this.processedAt, true);
    return this.createTaskAt(date);
  }

  get nextTask(): BaseTaskPlanned | undefined {
    return this.createTaskAt(this.nextDateFrom(dayjs()));
  }

  get lastTask(): BaseTaskPlanned | undefined {
    return this.allTasks
      .reverse()
      .find(task => dayjs().isBefore(task.occurredAt));
  }

  get allTasks(): BaseTaskPlanned[] {
    return this.tasksBetween(
      this.occurredAt,
      this.endDate || dayjs().add(1, "year")
    );
  }

  get processedTask(): BaseTaskPlanned[] {
    return this.tasksBetween(this.occurredAt, this.processedAt);
  }

  tasksBetween(
    startDate: Date | Dayjs,
    endDate: Date | Dayjs
  ): BaseTaskPlanned[] {
    return this.taskDatesBetween(startDate, endDate)
      .map(occurredAt => this.createTaskAt(occurredAt))
      .filter(t => Boolean(t)) as BaseTaskPlanned[];
  }

  taskDatesBetween(startDate: Date | Dayjs, endDate: Date | Dayjs): Date[] {
    const taskDates: Date[] = [];
    const loopEnd =
      this.endDate && dayjs(endDate).isAfter(this.endDate)
        ? this.endDate
        : endDate;
    let currentDate = dayjs(this.occurredAt);

    while (currentDate.isBetween(this.occurredAt, loopEnd, "day", "[]")) {
      if (currentDate.isBetween(startDate, endDate, "day", "[]")) {
        taskDates.push(currentDate.toDate());
      }
      currentDate = currentDate.add(
        this.timeFrequencyValue,
        this.timeFrequencyUnit as ManipulateType
      );
    }

    return taskDates;
  }

  private nextDateFrom(
    startDate: Date | Dayjs,
    excludeStartDay = true
  ): Date | undefined {
    const loopEnd = this.endDate || dayjs().add(1, "year");
    let currentDate = dayjs(this.occurredAt);

    while (currentDate.isBetween(this.occurredAt, loopEnd, "day", "[]")) {
      if (
        excludeStartDay
          ? currentDate.isAfter(startDate, "day")
          : currentDate.isSameOrAfter(startDate, "day")
      ) {
        return currentDate.toDate();
      }
      currentDate = currentDate.add(
        this.timeFrequencyValue,
        this.timeFrequencyUnit as ManipulateType
      );
    }

    return undefined;
  }

  private createTaskAt(occurredAt?: Date): BaseTaskPlanned | undefined {
    if (!occurredAt) return;

    return {
      isRecursiveTask: true,
      id: this.id,
      occurredAt,
      taskType: this.taskType,
      patient: this.patient,
      remindResponsible: this.remindResponsible,
      responsibleId: this.responsibleId,
      responsibleType: this.responsibleType,
      processingJournalEventType: this.processingJournalEventType,
      originTaskIdentifier: this.originTaskIdentifier,
      originJournalEvent: this.originJournalEvent,
      taskLabel: this.intl.t(`task.types.${this.taskType}`)
    };
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module "ember-data/types/registries/model" {
  export default interface ModelRegistry {
    "recursive-task-planned-journal-event": RecursiveTaskPlannedJournalEvent;
  }
}
